import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { getMetrics, type MetricRow } from "@/lib/metrics.functions";
import { AlertTriangle, Activity, CheckCircle2, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { InfoBox } from "@/components/app/info-box";
import { InfoTip } from "@/components/app/info-tip";

export const Route = createFileRoute("/_authenticated/metrics")({
  component: MetricsPage,
  head: () => ({ meta: [{ title: "Kennzahlen — InboxPulse" }] }),
});

const FAILURE_NAMES = new Set([
  "pixel_insert_failures_total",
  "pixel_select_failures_total",
  "drain_insert_failures_total",
  "drain_select_failures_total",
  "drain_update_failures_total",
  "drain_delete_failures_total",
  "drain_claim_failures_total",
]);

/** Label keys we surface as quick-filter chip groups. */
const QUICK_LABEL_KEYS = ["provider", "device", "table", "reason"] as const;
type QuickLabelKey = (typeof QUICK_LABEL_KEYS)[number];

function MetricsPage() {
  const fetchMetrics = useServerFn(getMetrics);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-metrics"],
    queryFn: () => fetchMetrics(),
    refetchInterval: 15_000,
  });

  const metrics: MetricRow[] = data?.metrics ?? [];

  // --- Filter state -------------------------------------------------------
  const [nameQuery, setNameQuery] = useState("");
  const [labelQuery, setLabelQuery] = useState("");
  // Per-key active values (multi-select within a key, AND across keys).
  const [activeLabels, setActiveLabels] = useState<Record<QuickLabelKey, Set<string>>>(() => ({
    provider: new Set(),
    device: new Set(),
    table: new Set(),
    reason: new Set(),
  }));

  const toggleLabel = (key: QuickLabelKey, value: string) => {
    setActiveLabels((prev) => {
      const next = { ...prev, [key]: new Set(prev[key]) };
      if (next[key].has(value)) next[key].delete(value);
      else next[key].add(value);
      return next;
    });
  };

  const clearFilters = () => {
    setNameQuery("");
    setLabelQuery("");
    setActiveLabels({
      provider: new Set(),
      device: new Set(),
      table: new Set(),
      reason: new Set(),
    });
  };

  // Discover available values per quick-filter key from the live data.
  const availableLabelValues = useMemo(() => {
    const out: Record<QuickLabelKey, Set<string>> = {
      provider: new Set(),
      device: new Set(),
      table: new Set(),
      reason: new Set(),
    };
    for (const m of metrics) {
      for (const k of QUICK_LABEL_KEYS) {
        const v = m.labels[k];
        if (typeof v === "string" && v.length > 0) out[k].add(v);
      }
    }
    return out;
  }, [metrics]);

  const filteredMetrics = useMemo(() => {
    const nq = nameQuery.trim().toLowerCase();
    const lq = labelQuery.trim().toLowerCase();
    return metrics.filter((m) => {
      if (nq && !m.name.toLowerCase().includes(nq)) return false;
      if (lq) {
        const flat = Object.entries(m.labels)
          .map(([k, v]) => `${k}=${v}`)
          .join(" ")
          .toLowerCase();
        if (!flat.includes(lq)) return false;
      }
      for (const k of QUICK_LABEL_KEYS) {
        const wanted = activeLabels[k];
        if (wanted.size === 0) continue;
        const v = m.labels[k];
        if (typeof v !== "string" || !wanted.has(v)) return false;
      }
      return true;
    });
  }, [metrics, nameQuery, labelQuery, activeLabels]);

  const activeFilterCount =
    (nameQuery.trim() ? 1 : 0) +
    (labelQuery.trim() ? 1 : 0) +
    QUICK_LABEL_KEYS.reduce((sum, k) => sum + activeLabels[k].size, 0);

  const { totals, byName, exposition } = useMemo(() => {
    const totals = { requests: 0, enqueued: 0, failures: 0 };
    const byName = new Map<string, MetricRow[]>();
    for (const m of filteredMetrics) {
      const list = byName.get(m.name) ?? [];
      list.push(m);
      byName.set(m.name, list);
      if (m.name === "pixel_requests_total") totals.requests += Number(m.value);
      if (m.name === "pixel_hits_enqueued_total") totals.enqueued += Number(m.value);
      if (FAILURE_NAMES.has(m.name)) totals.failures += Number(m.value);
    }
    return {
      totals,
      byName: Array.from(byName.entries()).sort(([a], [b]) => a.localeCompare(b)),
      exposition: toPrometheus(filteredMetrics),
    };
  }, [filteredMetrics]);

  const isAdminError = error instanceof Error && /forbidden|admin/i.test(error.message);

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <PageHeader
        title="Pipeline-Kennzahlen"
        description="Technische Zähler für den Tracking-Pixel und die Hintergrund-Verarbeitung. Aktualisiert sich automatisch alle 15 Sekunden."
        actions={
          <button
            onClick={() => refetch()}
            className="text-sm px-3 py-1.5 rounded-md border border-border hover:bg-accent transition disabled:opacity-50"
            disabled={isFetching}
            title="Sofort neu laden, ohne auf den 15-Sekunden-Takt zu warten"
          >
            {isFetching ? "Wird geladen…" : "Aktualisieren"}
          </button>
        }
      />

      <HelpPanel
        title="Hilfe: Technische Kennzahlen verstehen"
        what={
          <>
            Diese Seite zeigt die <b>Gesundheit der Technik im Hintergrund</b>: wie viele
            Pixel-Anfragen kommen rein, wie viele werden verarbeitet, wo stockt es?
          </>
        }
        why={
          <>
            Wenn das Dashboard plötzlich keine Öffnungen mehr zeigt, schaust du zuerst hier — meist
            sieht man sofort, wo das Problem klemmt.
          </>
        }
        how={[
          <>
            Zahlen lesen: <b>Eingang</b> = wieviele Pixel-Hits pro Stunde, <b>Warteschlange</b> =
            noch nicht verarbeitet, <b>Fehler</b> = fehlgeschlagen.
          </>,
          <>Aktualisiert automatisch alle 15 Sekunden.</>,
          <>
            Bei hoher Warteschlange (über 1000) → Backend ist überlastet, etwas warten oder Admin
            informieren.
          </>,
        ]}
        tips={[
          <>
            Eingang = 0 obwohl du Mails verschickst? Tracking-Pixel ist nicht eingebaut oder Domain
            falsch.
          </>,
          <>
            Fehler-Zähler steigt? Auf <b>Pixel Debug</b> einzelne Fehler ansehen.
          </>,
        ]}
        glossary={[
          {
            term: "Pipeline",
            def: "Der Verarbeitungs-Weg: Pixel-Hit → Warteschlange → Verarbeitung → Statistik.",
          },
          {
            term: "Warteschlange (Queue)",
            def: "Wartesaal für Hits, die noch nicht ausgewertet sind.",
          },
          { term: "Throughput", def: "Wieviel pro Zeit (z.B. Hits/Stunde)." },
        ]}
      />

      <InfoBox title="Was siehst du hier?">
        Diese Seite ist das <strong>technische Mess-Cockpit</strong> der App. Jedes Mal, wenn ein
        Empfänger eine Mail öffnet (Pixel wird geladen) oder im Hintergrund eine Aufgabe verarbeitet
        wird, erhöhen sich hier Zähler – wie ein Kilometerzähler im Auto. Du brauchst diese Seite
        vor allem, wenn etwas <strong>nicht funktioniert</strong>: An den Fehler-Zählern (
        <code>…_failures_total</code>) erkennst du sofort, wo es klemmt. Im Alltag reicht meist ein
        Blick auf die drei großen Kacheln oben. Die untere Box ('Prometheus-Export') ist nur für
        Monitoring-Tools wie Grafana relevant.
      </InfoBox>

      {isLoading && <Card className="p-6 text-muted-foreground">Kennzahlen werden geladen…</Card>}

      {error && (
        <Card className="p-6 border-destructive/50 bg-destructive/5 text-destructive flex items-start gap-3">
          <AlertTriangle className="size-5 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">
              {isAdminError
                ? "Admin-Rechte erforderlich"
                : "Kennzahlen konnten nicht geladen werden"}
            </div>
            <div className="text-sm opacity-80 mt-1">
              {isAdminError
                ? "Nur Nutzer mit der Rolle 'Admin' dürfen diese Seite sehen. Bitte einen Admin, dir die Rolle zuzuweisen."
                : error instanceof Error
                  ? error.message
                  : String(error)}
            </div>
          </div>
        </Card>
      )}

      {!isLoading && !error && (
        <>
          <Card className="p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Kennzahl-Name suchen (z. B. pixel_hits)"
                  value={nameQuery}
                  onChange={(e) => setNameQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Etiketten suchen (z. B. provider=Gmail, reason=error)"
                  value={labelQuery}
                  onChange={(e) => setLabelQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5">
              Tipp: Mit den Schnellfiltern unten klickst du Werte direkt an, statt sie zu tippen.
              <InfoTip>
                Ein <strong>Etikett</strong> (engl. 'Label') ist eine zusätzliche Info an einer
                Kennzahl. Beispiel: Die Kennzahl 'pixel_requests_total' hat das Etikett
                <code> provider=Gmail</code>, damit du Gmail von Outlook trennen kannst.
              </InfoTip>
            </div>
            <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
              {QUICK_LABEL_KEYS.map((key) => {
                const values = Array.from(availableLabelValues[key]).sort();
                if (values.length === 0) return null;
                const keyLabels: Record<QuickLabelKey, string> = {
                  provider: "Anbieter",
                  device: "Gerät",
                  table: "Tabelle",
                  reason: "Grund",
                };
                return (
                  <div key={key} className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-medium text-muted-foreground">
                      {keyLabels[key]}:
                    </span>
                    {values.map((v) => {
                      const active = activeLabels[key].has(v);
                      return (
                        <button
                          key={v}
                          onClick={() => toggleLabel(key, v)}
                          title={`Nur Einträge mit ${keyLabels[key]} = ${v} anzeigen`}
                          className={cn(
                            "text-xs px-2 py-0.5 rounded-full border transition",
                            active
                              ? "bg-primary text-primary-foreground border-primary"
                              : "border-border hover:bg-accent",
                          )}
                        >
                          {v}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
              {activeFilterCount > 0 && (
                <button
                  onClick={clearFilters}
                  title="Alle gesetzten Filter zurücksetzen"
                  className="ml-auto text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-border hover:bg-accent text-muted-foreground"
                >
                  <X className="size-3" />
                  Zurücksetzen ({activeFilterCount})
                </button>
              )}
            </div>
            <div className="mt-3 text-xs text-muted-foreground">
              Zeige {filteredMetrics.length.toLocaleString()} von {metrics.length.toLocaleString()}{" "}
              Zählern
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Stat
              icon={<Activity className="size-4" />}
              label="Pixel-Aufrufe"
              value={totals.requests}
              tip="Wie oft das unsichtbare 1×1-Tracking-Pixel insgesamt geladen wurde. Jeder Aufruf ist ein Hinweis darauf, dass eine Mail geöffnet wurde (oder vom Mail-Anbieter vorgeladen wurde)."
            />
            <Stat
              icon={<CheckCircle2 className="size-4 text-emerald-500" />}
              label="Treffer in Warteschlange"
              value={totals.enqueued}
              tip="Davon: echte Treffer, die zur weiteren Verarbeitung in die Warteschlange gelegt wurden (Bots/Vorlader werden vorher rausgefiltert)."
            />
            <Stat
              icon={<AlertTriangle className="size-4 text-destructive" />}
              label="Fehler gesamt"
              value={totals.failures}
              tone={totals.failures > 0 ? "danger" : "neutral"}
              tip="Summe aller Fehler in der Pipeline (Datenbank-Schreibfehler, Verarbeitungsfehler usw.). Sollte idealerweise 0 bleiben. Steigt der Wert, lohnt ein Blick in die Tabelle unten – die roten Zeilen zeigen, wo es klemmt."
            />
          </div>

          <Card className="p-0 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">
                    <span className="inline-flex items-center gap-1">
                      Kennzahl
                      <InfoTip>
                        Der technische Name des Zählers. Endungen mit
                        <code>_total</code> bedeuten: läuft immer nach oben, wird nie zurückgesetzt.
                      </InfoTip>
                    </span>
                  </th>
                  <th className="text-left px-4 py-2 font-medium">
                    <span className="inline-flex items-center gap-1">
                      Etiketten
                      <InfoTip>
                        Zusatz-Infos zum Zähler, z. B. um welchen Anbieter (Gmail/Outlook) oder
                        welches Gerät es geht.
                      </InfoTip>
                    </span>
                  </th>
                  <th className="text-right px-4 py-2 font-medium">Wert</th>
                  <th className="text-right px-4 py-2 font-medium">
                    <span className="inline-flex items-center gap-1">
                      Aktualisiert
                      <InfoTip>
                        Letzter Zeitpunkt, an dem dieser Zähler hochgezählt wurde. Sehr alte Zeiten
                        = der Zähler ist inaktiv.
                      </InfoTip>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {byName.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                      Noch keine Zähler erfasst. Sobald die erste Mail geöffnet wird, erscheinen
                      hier Werte.
                    </td>
                  </tr>
                )}
                {byName.flatMap(([name, rows]) =>
                  rows
                    .slice()
                    .sort((a, b) => Number(b.value) - Number(a.value))
                    .map((r, i) => {
                      const isFailure = FAILURE_NAMES.has(name);
                      return (
                        <tr
                          key={`${name}-${i}`}
                          className="border-t border-border/50"
                          title={
                            isFailure
                              ? "Fehler-Zähler – rote Zeilen zeigen, wo es Probleme gibt."
                              : undefined
                          }
                        >
                          <td
                            className={cn(
                              "px-4 py-2 font-mono text-xs",
                              isFailure ? "text-destructive" : "text-foreground",
                            )}
                          >
                            {name}
                          </td>
                          <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                            {formatLabels(r.labels)}
                          </td>
                          <td className="px-4 py-2 text-right font-mono tabular-nums">
                            {Number(r.value).toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-right text-xs text-muted-foreground">
                            {new Date(r.updated_at).toLocaleString("de-DE")}
                          </td>
                        </tr>
                      );
                    }),
                )}
              </tbody>
            </table>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-medium inline-flex items-center gap-1.5">
                Prometheus-Export
                <InfoTip>
                  <strong>Prometheus</strong> ist ein populäres Monitoring-Tool. Dieses Textformat
                  kannst du direkt in Grafana o. Ä. einlesen, um schöne Diagramme aus den Zählern zu
                  bauen. Wenn du kein Monitoring betreibst, kannst du diese Box ignorieren.
                </InfoTip>
              </h2>
              <button
                onClick={() => navigator.clipboard.writeText(exposition)}
                title="Den gesamten Text in die Zwischenablage kopieren"
                className="text-xs px-2 py-1 rounded border border-border hover:bg-accent"
              >
                Kopieren
              </button>
            </div>
            <pre className="text-xs font-mono bg-muted/40 rounded p-3 overflow-x-auto max-h-96">
              {exposition || "# Noch keine Kennzahlen vorhanden"}
            </pre>
          </Card>
        </>
      )}
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
  tone = "neutral",
  tip,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  tone?: "neutral" | "danger";
  tip?: string;
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
        {icon}
        <span>{label}</span>
        {tip && <InfoTip>{tip}</InfoTip>}
      </div>
      <div
        className={cn(
          "text-3xl font-semibold tabular-nums",
          tone === "danger" && value > 0 && "text-destructive",
        )}
      >
        {value.toLocaleString()}
      </div>
    </Card>
  );
}

function formatLabels(labels: Record<string, string>): string {
  const keys = Object.keys(labels);
  if (keys.length === 0) return "—";
  return keys
    .sort()
    .map((k) => `${k}="${labels[k]}"`)
    .join(", ");
}

function toPrometheus(metrics: MetricRow[]): string {
  const lines: string[] = [];
  const grouped = new Map<string, MetricRow[]>();
  for (const m of metrics) {
    const list = grouped.get(m.name) ?? [];
    list.push(m);
    grouped.set(m.name, list);
  }
  for (const [name, rows] of Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b))) {
    lines.push(`# TYPE ${name} counter`);
    for (const r of rows) {
      const labelStr = Object.keys(r.labels)
        .sort()
        .map((k) => `${k}="${escapeLabel(r.labels[k])}"`)
        .join(",");
      lines.push(`${name}${labelStr ? `{${labelStr}}` : ""} ${r.value}`);
    }
  }
  return lines.join("\n");
}

function escapeLabel(v: string): string {
  return v.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\n/g, "\\n");
}
